import { useStore } from '../state/store';
import { mockupStatusKind } from '../state/mockup';
import { ExportPrimary } from './ExportBar';

/**
 * The side panel's pinned footer: what the last render/build did, and the one
 * export this mode is for.
 *
 * Both used to be below the fold — the audit found the render status and the
 * export off the bottom of a 360-px panel at 1366x768, so the two things a shop
 * checks after every edit were the two things it had to scroll for. They are
 * the same elements, classes and `data-*` attributes as before (the e2e waits
 * read `.mockup-status`/`.sep-status`), moved out of the scrolling body and
 * into a row that cannot leave the screen. The `section` class is gone from the
 * status lines, because in the footer they are no longer sections.
 */
export function PanelFooter() {
  const appMode = useStore((s) => s.appMode);
  return (
    <div className="panel-footer">
      {appMode === 'mockup' ? <MockupStatus /> : appMode === 'sep' ? <SepStatus /> : <DtfStatus />}
      <ExportPrimary />
    </div>
  );
}

/** DTF mode's result: the pixels the pipeline produced, and at what density. */
function DtfStatus() {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  return <div className="panel-status muted small">{doc?.run ? `Output ${doc.run.width}×${doc.run.height} at ${Math.round(doc.run.dpi)} dpi` : ''}</div>;
}

/**
 * `data-state` and `data-generation` are the status as machine fact: the word on
 * screen is for the shop, and the render generation (the one the runner reserves
 * per render) is what a test waits on to know THIS render landed rather than the
 * one before it.
 */
function MockupStatus() {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const templateStatus = useStore((s) => s.templateStatus);
  const mockup = doc?.mockup;
  // The status itself is `mockupStatusKind` (state/mockup.ts), not a chain
  // written out here: the inspector's summary dot reads the same function, so
  // the dot and this line cannot disagree about whether something failed.
  const kind = mockupStatusKind(doc, templateStatus);
  const text = kind === 'no-run' ? 'Run the pipeline first'
    : kind === 'no-template' ? 'No template'
    : kind === 'preparing' ? 'Preparing template…'
    : kind === 'rendering' ? 'Rendering…'
    : kind === 'error' ? (mockup?.error ?? '')
    /* A render of an earlier version of this artwork: the file is there and looks
       finished, which is exactly why it has to say so. The warn colour and the plain
       words a shop can act on ("run it again"), not the machinery's "stale". */
    : kind === 'stale' ? 'Out of date'
    : kind === 'rendered' ? 'Rendered'
    : '';
  /* A render that ran out of its print area is not a failure — the file is there — but it is not
     plain news either, and the old lower-case "· clipped" beside "Rendered" read as a detail. It is
     the warn colour and its own word now, matching the line the Placement section shows under
     Width. Only for a render that actually landed: `clipped` is the last render's verdict. */
  const clipped = kind === 'rendered' && !!mockup?.clipped;
  /* The milliseconds are not the news — "did it render, and is it clipped" is.
     They stay one hover away (`title`) and machine-readable (`data-ms`). */
  const ms = kind === 'rendered' ? Math.round(mockup?.ms ?? 0) : undefined;
  return (
    <div
      className="panel-status mockup-status"
      data-state={kind}
      data-generation={mockup?.renderedGeneration}
      data-ms={ms}
      title={ms === undefined ? undefined : `Rendered in ${ms} ms`}
    >
      {kind === 'error' ? <span className="error">{text}</span>
        : kind === 'stale' ? <span className="warn-text">{text}</span>
        : text}
      {clipped && <> · <span className="warn-text">Clipped</span></>}
    </div>
  );
}

/**
 * The plate count is the fact a shop reads; the milliseconds are one hover away
 * (`title`) and machine-readable (`data-ms`), like the mockup's render status.
 * `data-state`/`data-generation` are what a test waits on to know THIS build
 * landed and not the one before it.
 */
function SepStatus() {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const sep = doc?.sep;
  const built = !!sep && sep.plates.length > 0 && !sep.building && !sep.error;
  return (
    <div
      className="panel-status sep-status"
      data-state={!sep ? 'none' : sep.error ? 'error' : sep.building ? 'building' : sep.plates.length ? 'built' : 'none'}
      data-generation={sep?.builtGeneration}
      data-ms={built ? Math.round(sep.ms ?? 0) : undefined}
      title={built ? `Built ${sep.plates.length} plates in ${Math.round(sep.ms ?? 0)} ms` : undefined}
    >
      {!sep ? ''
        : sep.error ? <span className="error">{sep.error}</span>
        : sep.building ? 'Building…'
        : sep.plates.length ? `Built ${sep.plates.length} plates`
        : ''}
      {/* A failed films-settings push is its own line: the plates on screen are
          still the last build's, so it never masks the build status above. */}
      {sep?.filmsError && <div className="small" style={{ color: 'var(--warn)' }}>Screening update failed: {sep.filmsError}</div>}
    </div>
  );
}
